// What has to hold before Submit is offered.
//
// Deliberately much shorter than `scoreSpec/validate.js`, and the difference is the point: the
// advanced editor validates a whole definition because the operator can edit all of it. Here the
// only editable surface is a name, a prompt and a description, so those are the only three things
// that can be wrong — everything else was already true of the source.
//
// The template check is the one that earns its place. `score_gen.prompt` is Jinja2 rendered with
// StrictUndefined, so a name outside the namespace does not come out empty: it raises, every unit
// of the run dies with template_render_failed, and a permanent error does not retry. It passes
// create AND lock — compilation only catches syntax — so the browser is the last place it can be
// caught at all. That makes it an error here, not the advice it is in the advanced editor, where
// the operator is assumed to be reading the whole spec.

import { runVariablesFor } from '@features/scoreSpec/specSchema';
import { unknownVariables } from '@features/scoreSpec/jinjaVars';
import { FEED_NAME_SEPARATOR } from '@features/scoreSpec/specSchema';
import { STEM_REGEX } from './naming';

// Field-keyed so each message can render under the control that owns it rather than in a pile at
// the bottom. Order is screen order.
export const validateDuplicateForm = ({ stem, prompt, description, documentsKind }) => {
  const errors = [];
  const stemValue = String(stem || '');

  if (!stemValue.trim()) {
    errors.push({ field: 'stem', message: 'Name is required.' });
  } else if (/_v\d+$/i.test(stemValue)) {
    errors.push({ field: 'stem', message: 'Leave the version off the name — it is added for you.' });
  } else if (FEED_NAME_SEPARATOR.test(stemValue)) {
    // Its own message rather than being folded into the charset one: `__` is legal in the character
    // class and banned for a different reason — the published feed name would become ambiguous.
    errors.push({
      field: 'stem',
      message:
        'Name cannot contain a double underscore — that is the separator a published feed name uses, so an id containing one cannot be resolved back to a spec and score.',
    });
  } else if (!STEM_REGEX.test(stemValue)) {
    errors.push({
      field: 'stem',
      message: 'Name must be lowercase letters, digits and underscores, starting with a letter.',
    });
  }

  if (!String(prompt || '').trim()) {
    errors.push({ field: 'prompt', message: 'Prompt cannot be empty.' });
  }

  if (!String(description || '').trim()) {
    errors.push({ field: 'description', message: 'Description is required.' });
  }

  const allowed = runVariablesFor(documentsKind);
  const unknown = unknownVariables(prompt, allowed);
  if (unknown.length > 0) {
    errors.push({
      field: 'prompt',
      message: `Unknown template variable${unknown.length > 1 ? 's' : ''}: ${unknown
        .map((v) => `{{${v}}}`)
        .join(', ')}. A ${documentsKind} run provides ${allowed.join(
        ', '
      )}. The render raises rather than coming out empty, and every unit of every run fails.`,
    });
  }

  return errors;
};

export const errorsFor = (errors, field) => errors.filter((e) => e.field === field).map((e) => e.message);
